<!-- Toolbar: search -->
<div class="dashboard-toolbar">
  <div class="dashboard-search">
    <kc-input
      [prefixIcon]="'pi pi-search'"
      [placeholder]="'Search by name, address, or covenant ID'"
      [type]="'text'"
      [ngModel]="dashboardSearch()"
      (valueChange)="onSearchChange($any($event))"
    ></kc-input>
  </div>
</div>

<!-- Filter row -->
<div class="filter-row">
  <div class="filter-chips">
    <button
      class="filter-chip"
      [class.active]="dashboardFilter() === 'all'"
      (click)="setDashboardFilter('all')"
    >
      All
    </button>
    <button
      class="filter-chip"
      [class.active]="dashboardFilter() === 'deadman'"
      (click)="setDashboardFilter('deadman')"
    >
      Dead Man's Switch
    </button>
    <button
      class="filter-chip"
      [class.active]="dashboardFilter() === 'timelock'"
      (click)="setDashboardFilter('timelock')"
    >
      Time Lock
    </button>
    <button
      class="filter-chip"
      [class.active]="dashboardFilter() === 'multisig'"
      (click)="setDashboardFilter('multisig')"
    >
      MultiSig
    </button>
    <button
      class="filter-chip"
      [class.active]="dashboardFilter() === 'escrow'"
      (click)="setDashboardFilter('escrow')"
    >
      Escrow
    </button>
    <button
      class="filter-chip"
      [class.active]="dashboardFilter() === 'selfcustody'"
      (click)="setDashboardFilter('selfcustody')"
    >
      Self-Custody
    </button>
  </div>
  <div
    class="status-toggle"
    role="tablist"
    aria-label="Filter contracts by status"
  >
    <button
      class="status-seg"
      role="tab"
      [attr.aria-selected]="statusFilter() === 'all'"
      [class.active]="statusFilter() === 'all'"
      (click)="setStatusFilter('all')"
    >
      All
    </button>
    <button
      class="status-seg"
      role="tab"
      [attr.aria-selected]="statusFilter() === 'active'"
      [class.active]="statusFilter() === 'active'"
      (click)="setStatusFilter('active')"
    >
      Active
    </button>
    <button
      class="status-seg"
      role="tab"
      [attr.aria-selected]="statusFilter() === 'history'"
      [class.active]="statusFilter() === 'history'"
      (click)="setStatusFilter('history')"
    >
      History
    </button>
  </div>
  <div class="filter-spacer"></div>
  <kc-button
    [text]="
      loading() ? 'Refreshing...' : indexerLoading() ? 'Syncing...' : 'Refresh'
    "
    [variant]="'secondary'"
    [size]="'s'"
    [isDisabled]="loading() || indexerLoading()"
    (buttonClick)="refreshRequested.emit()"
  ></kc-button>
</div>

<!-- Indexer unavailable / error banners -->
@if (indexerLoading() && !loading()) {
  <div class="banner info">
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
    <div class="banner-body">
      <div class="banner-title">Syncing indexer contracts</div>
      Local contracts are shown now. Contracts found only by the indexer will
      appear when this sync finishes.
    </div>
  </div>
}

@if (error()) {
  <div class="banner warn">
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path
        d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
      />
      <path d="M12 9v4M12 17h.01" />
    </svg>
    <div class="banner-body">
      <div class="banner-title">Indexer unavailable on {{ network() }}</div>
      {{ error() }} — showing locally cached contracts, which may be out of
      date.
    </div>
  </div>
}

<!-- Loading -->
@if (loading()) {
  <div class="skeleton-grid">
    @for (s of [1, 2, 3, 4]; track s) {
      <div class="skeleton-card">
        <div class="flex gap-12">
          <div class="sk avatar"></div>
          <div class="flex column gap-8 flex-1">
            <div class="sk line wide"></div>
            <div class="sk line narrow"></div>
          </div>
        </div>
        <div class="sk block"></div>
        <div class="sk line" style="height: 36px"></div>
      </div>
    }
  </div>
}

<!-- Empty -->
@if (!loading() && !indexerLoading() && contracts().length === 0) {
  <div class="state-block">
    <span class="sb-icon"
      ><svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M9 13h6M9 17h4" /></svg
    ></span>
    <div class="sb-title">No contracts yet</div>
    <div class="sb-text">
      Covenants let you lock KAS under on-chain rules — a dead-man's switch, a
      time lock, a multi-sig treasury, or an escrow. Create one or import a
      contract someone shared with you.
    </div>
    <div class="sb-actions">
      <button class="btn primary" (click)="tabRequested.emit('deploy')">
        Create a contract
      </button>
      <button
        class="btn secondary"
        (click)="tabRequested.emit('lookup-import')"
      >
        Import
      </button>
    </div>
  </div>
}

<!-- Cards -->
@if (!loading() && contracts().length > 0) {
  <div class="card-grid">
    @for (contract of contracts(); track contract.id) {
      <div
        class="contract-card"
        [attr.data-tpl]="display.getTemplateKey(contract)"
        [class.is-spent]="contract.status === 'spent'"
      >
        <div class="card-head">
          <span class="tpl-icon">
            @switch (display.getTemplateKey(contract)) {
              @case ("deadman") {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              }
              @case ("timelock") {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
              }
              @case ("multisig") {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="9" cy="9" r="3" />
                  <path
                    d="M15 6a3 3 0 0 1 0 6M4 20c0-2.8 2.2-5 5-5s5 2.2 5 5M16 15c2.2.4 4 2.4 4 5"
                  />
                </svg>
              }
              @case ("escrow") {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M12 3v18M4 7h16M7 7l-3 6a3 3 0 0 0 6 0zM17 7l-3 6a3 3 0 0 0 6 0z"
                  />
                </svg>
              }
              @case ("selfcustody") {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M12 3l7 3v5c0 4.5-2.9 8.4-7 10-4.1-1.6-7-5.5-7-10V6z"
                  />
                  <path d="M9 12l2 2 4-5" />
                </svg>
              }
              @default {
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                >
                  <circle cx="12" cy="12" r="9" />
                </svg>
              }
            }
          </span>
          <div class="card-title-wrap">
            <h3 class="card-title">{{ contract.displayName }}</h3>
            <span class="card-sub"
              >{{ contract.contractTypeLabel }} -
              {{ display.getSourceLabel(contract) }}</span
            >
            @if (canEditContractAlias(contract)) {
              @if (editingAliasKey() === getAliasEditKey(contract)) {
                <div class="alias-editor">
                  <input
                    class="alias-input"
                    type="text"
                    placeholder="Contract nickname"
                    [(ngModel)]="aliasDraft"
                  />
                  <button
                    class="alias-btn"
                    type="button"
                    (click)="saveAlias(contract)"
                  >
                    Save
                  </button>
                  <button
                    class="alias-btn ghost"
                    type="button"
                    (click)="cancelAliasEdit()"
                  >
                    Cancel
                  </button>
                </div>
              } @else {
                <div class="alias-actions">
                  <button
                    class="alias-link"
                    type="button"
                    (click)="beginAliasEdit(contract)"
                  >
                    {{ contract.aliasName ? "Edit nickname" : "Add nickname" }}
                  </button>
                  @if (contract.aliasName) {
                    <button
                      class="alias-link muted"
                      type="button"
                      (click)="removeAlias(contract)"
                    >
                      Remove nickname
                    </button>
                  }
                </div>
                @if (aliasNotice()?.key === getAliasEditKey(contract)) {
                  <div class="alias-notice">
                    {{ aliasNotice()!.message }}
                  </div>
                }
              }
              @if (canRemoveTrackedContract(contract)) {
                <button
                  class="alias-link danger"
                  type="button"
                  (click)="removeTrackedContract(contract)"
                >
                  Remove contract
                </button>
              }
            } @else {
              <div class="alias-notice">
                {{ getAliasUnavailableMessage() }}
              </div>
            }
          </div>
          @if (contract.status === "spent") {
            <span class="status-badge spent">Spent</span>
          } @else if (
            contract.status === "unknown" ||
            contract.status === "tracking-incomplete"
          ) {
            <span
              class="status-badge tracking"
              kcTooltip="Indexer tracking is incomplete"
              tooltipPosition="left-center"
              >Tracking</span
            >
          } @else {
            <span class="status-badge active">Active</span>
          }
        </div>

        <div class="card-stats">
          <div class="stat">
            <span class="stat-label">Locked</span>
            <span class="stat-value"
              >{{ display.formatSompiToKas(contract.amountSompi) }}
              <small>KAS</small></span
            >
          </div>
          @if (contract.deadlineMs) {
            <div class="stat">
              <span class="stat-label">Deadline</span>
              <span class="stat-value sm">{{
                display.formatTimestamp(contract.deadlineMs)
              }}</span>
            </div>
          }
        </div>

        @if (contract.covenantId || contract.currentAddress) {
          <div class="card-id">
            <span class="card-id-label">{{
              contract.covenantId ? "Covenant ID" : "Address"
            }}</span>
            @if (
              contract.covenantId &&
                display.getCovenantExplorerLink(contract.covenantId);
              as covenantLink
            ) {
              <a
                class="card-id-value"
                [href]="covenantLink"
                target="_blank"
                rel="noopener noreferrer"
                [title]="contract.covenantId"
                >{{ display.truncate(contract.covenantId, 22) }}</a
              >
            } @else {
              <span
                class="card-id-value"
                [title]="contract.covenantId || contract.currentAddress"
                >{{
                  display.truncate(
                    contract.covenantId || contract.currentAddress || "",
                    22
                  )
                }}</span
              >
            }
            <app-copy-button
              [value]="contract.covenantId || contract.currentAddress || ''"
              [size]="'sm'"
            ></app-copy-button>
          </div>
        }

        @if (getVisibleRoles(contract).length > 0) {
          <div class="role-chips">
            @for (role of getVisibleRoles(contract).slice(0, 3); track role) {
              <span
                class="role-chip"
                [class]="'role-chip ' + getRoleClass(role)"
                >{{ role }}</span
              >
            }
          </div>
        }

        <div class="card-latest">
          <span class="muted">Latest</span>
          <span>{{
            display.formatActionName(contract.latestAction || "deploy")
          }}</span>
          @if (contract.latestTxid) {
            <a
              class="push text-blue"
              [href]="display.getExplorerLink(contract.latestTxid)"
              target="_blank"
              rel="noopener noreferrer"
              >{{ display.truncate(contract.latestTxid, 12) }}</a
            >
          }
        </div>

        <div class="card-actions">
          <button
            class="btn secondary"
            (click)="contractDetailsRequested.emit(contract)"
          >
            Details
          </button>
          @if (contract.status !== "spent") {
            <button
              class="btn action"
              (click)="contractActionRequested.emit(contract)"
            >
              {{ contract.nextActionLabel }}
            </button>
          }
        </div>
      </div>
    }
  </div>
}
